/**
 * PC 活动主页：四模块 Tab 与路由映射
 * 对齐 H5 activityHome，path 按 PC 现网（taskList 小写等）
 */
import {
  getActivityDetailRoute,
  normalizeActivityNameEn,
  openHashRouteInNewTab
} from '@/utils/activityShortRoute'

/** 课程任务类型：活动任务页不展示，学习中心展示 */
export const COURSE_TASK_TYPE = 43
/** 任务卡类型：点击岛屿节点弹详情 + 子任务列表 */
export const TASK_CARD_TYPE = 33
/** 缴费任务 */
export const PAY_TASK_TYPE = 2
/** oms：当前任务条遇到「已完成过缴费」后再碰到缴费，则顺延到非缴费 */
export const OMS_CURRENT_TASK_NAME_EN = 'oms'

/** 赛程色循环：蓝 / 红 / 紫 / 绿 / 黄，与岛屿背景、弹窗稿一一对应 */
export const SECTION_THEME_KEYS = ['blue', 'red', 'purple', 'green', 'yellow']

export function getSectionThemeKey (sIdx) {
  const keys = SECTION_THEME_KEYS
  const i = ((Number(sIdx) || 0) % keys.length + keys.length) % keys.length
  return keys[i]
}

export function isCourseTask (task) {
  return task != null && +task.taskType === COURSE_TASK_TYPE
}

export function isTaskCard (task) {
  return task != null && +task.taskType === TASK_CARD_TYPE
}

export function isPayTask (task) {
  return task != null && +task.taskType === PAY_TASK_TYPE
}

/** 列表/气泡跳过按钮：只看后端算好的 canSkip，不要用 isRequired 判断任务卡 */
export function canSkipTask (task) {
  return !!(task && +task.canSkip === 1)
}

export const MODULE_DETAIL = 'detail'
export const MODULE_TASK = 'task'
export const MODULE_LEARN = 'learn'
export const MODULE_NOTICE = 'notice'

export const MODULE_TABS = [
  { key: MODULE_DETAIL, labelKey: 'flowActive91' },
  { key: MODULE_TASK, labelKey: 'flowActive6' },
  { key: MODULE_LEARN, labelKey: 'flowActive92' },
  { key: MODULE_NOTICE, labelKey: 'flowActive93' }
]

/** joinCount > 0 视为已报名 */
export function isJoined (contestData) {
  return !!(contestData && +contestData.joinCount > 0)
}

/**
 * 报名阶段三态（基于 enterStartTime / enterEndTime 与 nowDate 对比）
 * @returns 'before'  | (1) now < enterStartTime  即将开始，置灰不可点
 *                   | (2) 开始结束时间均未配置（未配置报名） => 也视为即将开始，置灰不可点
 *          'going'    | enterStartTime ≤ now ≤ enterEndTime（或仅一端有配置且落在报名区间） | 立即报名，可点
 *          'ended'    | now > enterEndTime   | 报名已结束，置灰不可点
 */
export function getSignupPhase (contestData, nowDate) {
  const d = contestData
  if (!d) return 'before'
  const now = Number(nowDate) || Date.now()
  const start = Number(d.enterStartTime)
  const end = Number(d.enterEndTime)
  if (!start && !end) return 'before'
  if (start && now < start) return 'before'
  if (end && now > end) return 'ended'
  return 'going'
}

/** 报名阶段 → 对应按钮文案 key（$t(...) 里用） */
export function getSignupBtnTextKey (phase) {
  if (phase === 'before') return 'flowActive9'
  if (phase === 'ended') return 'flowActive7'
  return 'flowActive8'
}

/** 报名阶段 → 按钮是否禁用（即将开始 / 报名已结束均禁用） */
export function isSignupBtnDisabled (phase) {
  return phase === 'before' || phase === 'ended'
}

/** 客户端是否已登录：优先 vuex login_bol，否则解析 localStorage.userData */
export function isActivityLoggedIn (loginBol) {
  if (loginBol) return true
  try {
    let userData = localStorage.getItem('userData')
    if (!userData) return false
    userData = JSON.parse(userData)
    userData = userData && userData.data ? JSON.parse(userData.data) : userData
    return !!userData
  } catch (e) {
    return false
  }
}

/** 已登录且已报名：才可请求活动任务 / 学习中心 / 活动通知业务接口 */
export function canFetchJoinedModuleApis (contestData, loginBol) {
  return isActivityLoggedIn(loginBol) && isJoined(contestData)
}

/** LOGIN_STATE 短缓存，避免切 Tab 时 chrome / all-btn 重复打 */
let loginStateCache = null
let loginStateInflight = null
const LOGIN_STATE_TTL_MS = 3000

/**
 * 静默校验是否登录（limitPostNoLogin，10016 不弹登录框）
 * @returns {Promise<boolean>}
 */
export function fetchActivityLoginState (vm) {
  const now = Date.now()
  if (loginStateCache && (now - loginStateCache.ts) < LOGIN_STATE_TTL_MS) {
    return Promise.resolve(loginStateCache.value)
  }
  if (loginStateInflight) return loginStateInflight

  const promise = new Promise(resolve => {
    const api = vm && vm.$api
    const fn = api && (typeof api.limitPostNoLogin === 'function'
      ? api.limitPostNoLogin
      : (typeof api.limitPost === 'function' ? api.limitPost : null))
    if (!fn) {
      const fallback = isActivityLoggedIn(
        vm && vm.$store && vm.$store.state && vm.$store.state.login
          ? vm.$store.state.login.isLogin
          : false
      )
      loginStateCache = { ts: Date.now(), value: fallback }
      resolve(fallback)
      return
    }
    const finish = (ok) => {
      const loggedIn = !!ok
      try {
        if (vm.$store) vm.$store.commit('set_isLogin', loggedIn)
      } catch (e) { /* ignore */ }
      loginStateCache = { ts: Date.now(), value: loggedIn }
      resolve(loggedIn)
    }
    fn.call(api, 'LOGIN_STATE', {}, (res) => {
      finish(res && +res.code === 0)
    }, () => {
      finish(false)
    }, () => {
      finish(false)
    })
  })
  loginStateInflight = promise.then(
    v => { loginStateInflight = null; return v },
    e => { loginStateInflight = null; throw e }
  )
  return loginStateInflight
}

/** 清登录态短缓存（如主动登出后） */
export function invalidateActivityLoginStateCache () {
  loginStateCache = null
  loginStateInflight = null
}

export function getRouteSeasonId (route) {
  if (!route) return ''
  return (
    (route.params && route.params.seasonId) ||
    (route.query && route.query.seasonId) ||
    (route.meta && route.meta.seasonId) ||
    ''
  )
}

export function getRouteContestId (route) {
  if (!route) return ''
  return (
    (route.query && route.query.contestId) ||
    (route.meta && route.meta.contestId) ||
    ''
  )
}

/**
 * 从路由 + 赛季信息拼出模块跳转上下文
 */
export function getActivityHomeContext (route, contestData) {
  const seasonId =
    getRouteSeasonId(route) ||
    (contestData && contestData.seasonId) ||
    ''
  const contestId =
    getRouteContestId(route) ||
    (contestData && contestData.contestId) ||
    ''
  const nameEn = normalizeActivityNameEn(
    (contestData && contestData.nameEn) ||
    (route.params && route.params.gameName) ||
    (route.meta && route.meta.activityShort && route.name) ||
    ''
  )
  return {
    seasonId: seasonId ? String(seasonId) : '',
    contestId: contestId ? String(contestId) : '',
    nameEn
  }
}

/** 根据当前路由推断所属模块 Tab */
export function getModuleKeyByRoute (route) {
  if (!route) return MODULE_DETAIL
  const name = route.name
  if (name === 'taskList' || name === 'TaskList' || name === 'taskListIsland') return MODULE_TASK
  if (name === 'LearningCenter') return MODULE_LEARN
  if (
    name === 'ActivityNotice' ||
    name === 'ActivityNoticeAcademic' ||
    name === 'ActivityNoticeActivity' ||
    name === 'teamMessage' ||
    (route.path && String(route.path).indexOf('/ActivityNotice/') === 0)
  ) {
    return MODULE_NOTICE
  }
  if (name === 'GameData' || (route.meta && route.meta.activityShort)) return MODULE_DETAIL
  return MODULE_DETAIL
}

/**
 * 是否活动主页壳（四 Tab）路由。
 * HeaderBox 仅在此类路由上启用「下滑隐藏」交互，不影响首页等其他页。
 */
export function isActivityHomeChromeRoute (route) {
  if (!route) return false
  if (route.meta && route.meta.activityHomeChrome) return true
  const name = route.name
  if (
    name === 'taskList' ||
    name === 'TaskList' ||
    name === 'taskListIsland' ||
    name === 'LearningCenter' ||
    name === 'ActivityNotice'
  ) {
    return true
  }
  if (name === 'GameData' || (route.meta && route.meta.activityShort)) return true
  const path = route.path ? String(route.path) : ''
  return (
    path.indexOf('/taskList/') === 0 ||
    path.indexOf('/taskListIsland/') === 0 ||
    path.indexOf('/LearningCenter/') === 0 ||
    path.indexOf('/ActivityNotice/') === 0 ||
    path.indexOf('/GameData/') === 0
  )
}

/**
 * 构建模块跳转路由
 * 详情：短链优先；通知：ActivityNotice；任务/学习：带 seasonId
 */
export function buildModuleRoute (moduleKey, ctx) {
  const seasonId = ctx.seasonId
  const contestId = ctx.contestId
  const nameEn = ctx.nameEn
  const query = {}
  if (contestId) query.contestId = contestId
  if (nameEn) query.nameEn = nameEn

  switch (moduleKey) {
    case MODULE_TASK:
      return {
        path: `/taskList/${seasonId}`,
        query: { ...query }
      }
    case MODULE_LEARN:
      return {
        path: `/LearningCenter/${seasonId}`,
        query: { ...query }
      }
    case MODULE_NOTICE:
      return {
        path: `/ActivityNotice/${seasonId}`,
        query: {
          ...query,
          seasonId,
          topicId: seasonId,
          tab: 'notice'
        }
      }
    case MODULE_DETAIL:
    default: {
      // 短链：仅 path、无 query；老链接 /GameData/:nameEn 才带 contestId/seasonId
      return getActivityDetailRoute({
        nameEn: nameEn || 'Activity',
        contestId,
        seasonId
      })
    }
  }
}

/**
 * 统一入口路由（对齐 H5）：调用方传入 joined/joinCount；
 * joined===true 或 joinCount>0 且有 seasonId → 壳子活动任务，否则 → 详情。不补拉 GETINFO。
 * @param {{ nameEn?, contestId?, seasonId?, joined?, joinCount? }} payload
 */
export function getActivityHomeEntryRoute (payload) {
  const {
    nameEn,
    contestId,
    seasonId,
    joined,
    joinCount
  } = payload || {}
  const isJoinedFlag = joined === true || +joinCount > 0
  if (isJoinedFlag && seasonId) {
    return buildModuleRoute(MODULE_TASK, {
      seasonId: String(seasonId),
      contestId: contestId != null ? String(contestId) : '',
      nameEn: normalizeActivityNameEn(nameEn)
    })
  }
  return getActivityDetailRoute({ nameEn, contestId, seasonId })
}

/** 当前页跳转活动主页入口（router.push） */
export function gotoActivityHome (vmOrRouter, payload) {
  const route = getActivityHomeEntryRoute(payload)
  const router = vmOrRouter && vmOrRouter.$router ? vmOrRouter.$router : vmOrRouter
  if (!router || typeof router.push !== 'function') return route
  return router.push(route)
}

/** 新窗口打开活动主页入口 */
export function openActivityHomeInNewTab (payload) {
  return openHashRouteInNewTab(getActivityHomeEntryRoute(payload))
}

/** 用户主动点 Tab 的一次性标记（刷新即丢） */
let pendingActivityHomeUserTab = null

export function markActivityHomeUserTabSwitch (seasonId, moduleKey) {
  pendingActivityHomeUserTab = {
    seasonId: seasonId != null ? String(seasonId) : '',
    moduleKey
  }
}

export function consumeActivityHomeUserTabSwitch (seasonId, moduleKey) {
  const pending = pendingActivityHomeUserTab
  pendingActivityHomeUserTab = null
  if (!pending) return false
  const sid = seasonId != null ? String(seasonId) : ''
  return pending.seasonId === sid && pending.moduleKey === moduleKey
}

function taskOriginOrder (task) {
  if (task && task.originIndex != null && task.originIndex !== '') return Number(task.originIndex)
  if (task && task.index != null && task.index !== '') return Number(task.index)
  return Number(task && task.taskSort) || 0
}

function remapRequirementIndex (task, idToDisplayIndex, originToDisplayIndex) {
  const req = task && task.requirementTask
  if (!req) return task
  const idKey = req.taskRelationId != null ? String(req.taskRelationId) : ''
  const mapped = (idKey && idToDisplayIndex[idKey] != null)
    ? idToDisplayIndex[idKey]
    : (req.index != null ? originToDisplayIndex[req.index] : null)
  if (mapped == null) return task
  return {
    ...task,
    requirementTask: { ...req, originIndex: req.index, index: mapped }
  }
}

/** 按 taskRelationId 在展示列表中取前端序号（如 1.2） */
export function getDisplayIndexByRelationId (sectionList, taskRelationId) {
  if (!Array.isArray(sectionList) || taskRelationId == null || taskRelationId === '') return ''
  const id = String(taskRelationId)
  for (let s = 0; s < sectionList.length; s++) {
    const cats = (sectionList[s] && sectionList[s].categoryList) || []
    for (let c = 0; c < cats.length; c++) {
      const tasks = (cats[c] && cats[c].taskCardList) || []
      for (let i = 0; i < tasks.length; i++) {
        const t = tasks[i]
        if (t && t.taskRelationId != null && String(t.taskRelationId) === id) {
          return t.index != null ? t.index : ''
        }
      }
    }
  }
  return ''
}

/**
 * 过滤掉课程任务后的赛程列表（活动任务页用）
 * 赛程即使没有非课程任务也保留（供侧栏切换 + 岛屿空场景）；分类下没有任务则去掉
 * 展示序号按全量可见分类连续编号为「分类号.任务号」
 */
export function filterSectionsExcludeCourses (sectionList) {
  if (!Array.isArray(sectionList)) return []
  const idToDisplayIndex = {}
  const originToDisplayIndex = {}
  let catNo = 0
  const sections = sectionList.map(section => {
    const visibleCats = (section.categoryList || []).map(cat => {
      const tasks = (cat.taskCardList || [])
        .filter(t => !isCourseTask(t))
        .slice()
        .sort((a, b) => taskOriginOrder(a) - taskOriginOrder(b))
      return tasks.length ? { cat, tasks } : null
    }).filter(Boolean)
    const categoryList = visibleCats.map(item => {
      catNo += 1
      const taskCardList = item.tasks.map((t, i) => {
        const index = catNo + '.' + (i + 1)
        if (t.taskRelationId != null) idToDisplayIndex[String(t.taskRelationId)] = index
        if (t.index != null) originToDisplayIndex[t.index] = index
        return { ...t, originIndex: t.index, index }
      })
      return { ...item.cat, taskCardList }
    })
    return { ...section, categoryList }
  })
  return sections.map(section => ({
    ...section,
    categoryList: (section.categoryList || []).map(cat => ({
      ...cat,
      taskCardList: (cat.taskCardList || []).map(task =>
        remapRequirementIndex(task, idToDisplayIndex, originToDisplayIndex)
      )
    }))
  }))
}

/** 只保留课程任务的赛程列表（学习中心用） */
export function filterSectionsOnlyCourses (sectionList) {
  if (!Array.isArray(sectionList)) return []
  return sectionList.map(section => {
    const categoryList = (section.categoryList || [])
      .map(cat => ({
        ...cat,
        taskCardList: (cat.taskCardList || []).filter(t => isCourseTask(t))
      }))
      .filter(cat => cat.taskCardList && cat.taskCardList.length > 0)
    return { ...section, categoryList }
  }).filter(section => section.categoryList && section.categoryList.length > 0)
}

/** 课程任务上的 curriculaPlanId（businessId 优先） */
export function getCourseCurriculaPlanId (task) {
  if (!task) return ''
  const fromBiz =
    task.taskBusinessListDto &&
    task.taskBusinessListDto[0] &&
    task.taskBusinessListDto[0].businessId
  if (fromBiz != null && fromBiz !== '') return String(fromBiz)
  if (task.curriculaPlanId != null && task.curriculaPlanId !== '') {
    return String(task.curriculaPlanId)
  }
  return ''
}

/** 从任务卡赛程里收集 type=43 的课程计划 id（去重、保序） */
export function collectCourseCurriculaPlanIds (sectionList) {
  const ids = []
  const seen = Object.create(null)
  filterSectionsOnlyCourses(sectionList).forEach(section => {
    ;(section.categoryList || []).forEach(cat => {
      ;(cat.taskCardList || []).forEach(task => {
        const id = getCourseCurriculaPlanId(task)
        if (!id || seen[id]) return
        seen[id] = true
        ids.push(id)
      })
    })
  })
  return ids
}

/** 把 curriculaPlan/list 结果合并进任务卡（保留任务态/跳转，补课程展示字段） */
export function mergeCurriculaPlanListIntoSections (sectionList, planList) {
  const map = Object.create(null)
  ;(planList || []).forEach(plan => {
    if (!plan) return
    const id = plan.curriculaPlanId != null ? plan.curriculaPlanId : plan.id
    if (id == null || id === '') return
    map[String(id)] = plan
  })
  if (!Object.keys(map).length) return sectionList || []

  return (sectionList || []).map(section => ({
    ...section,
    categoryList: (section.categoryList || []).map(cat => ({
      ...cat,
      taskCardList: (cat.taskCardList || []).map(task => {
        const id = getCourseCurriculaPlanId(task)
        const plan = id ? map[id] : null
        if (!plan) return task
        return attachCurriculaPlanToCourseTask(task, plan)
      })
    }))
  }))
}

function attachCurriculaPlanToCourseTask (task, plan) {
  const next = { ...task, curriculaPlan: plan }
  if (task.endTime != null) next.taskEndTime = task.endTime
  if (task.startTime != null) next.taskStartTime = task.startTime
  if (plan.curriculaPlanName) next.taskTitle = plan.curriculaPlanName
  if (plan.curriculaPlanNameEn) next.taskTitleEn = plan.curriculaPlanNameEn
  const cover =
    plan.curriculaPlanImgUrl || plan.coverImg || plan.coverImgPath || ''
  if (cover) {
    next.coverImgPath = cover
    if (next.taskBusinessListDto && next.taskBusinessListDto[0]) {
      next.taskBusinessListDto = [
        {
          ...next.taskBusinessListDto[0],
          coverImg: cover,
          businessId:
            next.taskBusinessListDto[0].businessId != null
              ? next.taskBusinessListDto[0].businessId
              : plan.curriculaPlanId
        }
      ]
    }
  }
  if (plan.completeProgress != null) next.completeProgress = plan.completeProgress
  else if (plan.progress != null) next.completeProgress = plan.progress
  if (plan.sectionTotal != null) next.sectionTotal = plan.sectionTotal
  if (plan.completeCount != null) next.completeCount = plan.completeCount
  if (plan.endTime != null) next.endTime = plan.endTime
  if (plan.startTime != null) next.startTime = plan.startTime
  if (plan.statusDesc != null) next.statusDesc = plan.statusDesc
  if (plan.statusDescEn != null) next.statusDescEn = plan.statusDescEn
  if (plan.jumpUrl) next.jumpUrl = plan.jumpUrl
  if (plan.isRequired != null) next.isRequired = plan.isRequired
  if (plan.required != null) next.required = plan.required
  if (plan.isOptional != null) next.isOptional = plan.isOptional
  return next
}

/**
 * 学习中心：用任务卡里的课程 id 拉 curriculaPlan/list
 * @returns {Promise<Array>} 合并后的 sectionList（失败则回落任务卡原数据）
 */
export function fetchLearningCenterCourseSections (vm, seasonId, sectionList) {
  const list = sectionList || []
  const sid = seasonId != null ? String(seasonId) : ''
  const curriculaPlanIds = collectCourseCurriculaPlanIds(list)
  if (!sid || !curriculaPlanIds.length) {
    return Promise.resolve(list)
  }
  if (!vm || !vm.$api || typeof vm.$api.post !== 'function') {
    return Promise.resolve(list)
  }
  return new Promise(resolve => {
    vm.$api.post(
      'CURRICULAPLAN_LIST',
      { seasonId: sid, curriculaPlanIds },
      res => {
        const plans =
          (res && res.data && (res.data.list || res.data.curriculaPlanList || res.data)) ||
          []
        const planArr = Array.isArray(plans) ? plans : []
        resolve(mergeCurriculaPlanListIntoSections(list, planArr))
      },
      () => resolve(list),
      () => resolve(list)
    )
  })
}

/**
 * 吸底「当前任务」推荐：按接口返回顺序取第一个可做的非课程一级任务（对齐 H5）
 * 排除：status=2 已完成；3/5/7 未解锁/已结束等；前置任务缺失
 * 注意：不走带排序的 filterSectionsExcludeCourses，避免与 H5「接口顺序」不一致
 *
 * oms（nameEn === 'oms'）额外：若第一个可做的是缴费任务，且列表更前面已有完成的缴费任务，
 * 则顺延到后面第一个可做的非缴费（非课程）任务。
 * @param {Array} sectionList
 * @param {string} [nameEn]
 */
export function pickCurrentTask (sectionList, nameEn) {
  if (!Array.isArray(sectionList)) return null
  const omsSkipPay = String(nameEn || '') === OMS_CURRENT_TASK_NAME_EN
  let firstDoable = null
  let sawCompletedPay = false

  for (let s = 0; s < sectionList.length; s++) {
    const cats = (sectionList[s] && sectionList[s].categoryList) || []
    for (let c = 0; c < cats.length; c++) {
      const tasks = (cats[c] && cats[c].taskCardList) || []
      for (let t = 0; t < tasks.length; t++) {
        const task = tasks[t]
        if (!task || isCourseTask(task)) continue

        if (!firstDoable) {
          if (isDoablePrimaryTask(task)) {
            firstDoable = task
            // 非 oms，或第一个可做不是缴费，或前面没有已完成缴费 → 用原逻辑
            if (!omsSkipPay || !isPayTask(task) || !sawCompletedPay) {
              return task
            }
            // oms：继续向后找第一个可做的非缴费任务
            continue
          }
          if (isPayTask(task) && +task.status === 2) {
            sawCompletedPay = true
          }
          continue
        }

        if (isDoablePrimaryTask(task) && !isPayTask(task)) {
          return task
        }
      }
    }
  }
  return firstDoable
}

function isDoablePrimaryTask (task) {
  if (!task) return false
  if (+task.status === 2) return false
  if ([3, 5, 7].indexOf(+task.status) !== -1) return false
  if (task.requirementTask && +task.requirementTask.status === 8) return false
  return true
}

/** 在过滤后的赛程列表里按 taskRelationId 定位岛屿节点 */
export function findTaskLocation (sections, taskRelationId) {
  if (!taskRelationId) return null
  const list = sections || []
  for (let sIdx = 0; sIdx < list.length; sIdx++) {
    const cats = (list[sIdx] && list[sIdx].categoryList) || []
    for (let cIdx = 0; cIdx < cats.length; cIdx++) {
      const tasks = (cats[cIdx] && cats[cIdx].taskCardList) || []
      for (let tIdx = 0; tIdx < tasks.length; tIdx++) {
        if (tasks[tIdx] && tasks[tIdx].taskRelationId === taskRelationId) {
          return { sIdx, cIdx, tIdx, task: tasks[tIdx] }
        }
      }
    }
  }
  return null
}

/** 是否即将截止（截止时间在未来 72h 内） */
export function isTaskDueSoon (task, now) {
  if (!task || !task.endTime) return false
  const left = +task.endTime - now
  return left > 0 && left <= 3 * 24 * 60 * 60 * 1000
}

/**
 * 任务节点展示态
 * - done：已完成 (status=2)
 * - disabled：未开启/已结束/前置缺失 (3/5/7 或 requirementTask.status=8)
 * - active：可进行
 */
export function getTaskNodeVisualState (task) {
  if (!task) return 'disabled'
  const s = +task.status
  if (s === 2) return 'done'
  if (s === 3 || s === 5 || s === 7) return 'disabled'
  if (task.requirementTask && +task.requirementTask.status === 8) return 'disabled'
  return 'active'
}

// ---------- 请求缓存（模块级单例，跨 Tab 复用） ----------
const contestInfoCache = Object.create(null)
const contestInfoInflight = Object.create(null)
const modeCache = Object.create(null)
const modeInflight = Object.create(null)
const unreadCache = Object.create(null)
const unreadInflight = Object.create(null)
const unreadNoticeInflight = Object.create(null)
const taskCardCache = Object.create(null)
const taskCardInflight = Object.create(null)
const relationListCache = Object.create(null)
const relationListInflight = Object.create(null)
const detailContentCache = Object.create(null)
const detailContentInflight = Object.create(null)

function contestCacheKey ({ seasonId, contestId }) {
  if (seasonId) return `season:${String(seasonId)}`
  if (contestId) return `contest:${String(contestId)}`
  return ''
}

function clearKeyed (cache, inflight, key) {
  if (key) {
    delete cache[key]
    delete inflight[key]
    return
  }
  Object.keys(cache).forEach(k => { delete cache[k] })
  Object.keys(inflight).forEach(k => { delete inflight[k] })
}

function clearAllActivityHomeCaches () {
  clearKeyed(contestInfoCache, contestInfoInflight)
  clearKeyed(modeCache, modeInflight)
  clearKeyed(unreadCache, unreadInflight)
  clearKeyed(unreadNoticeInflight, unreadNoticeInflight)
  clearKeyed(taskCardCache, taskCardInflight)
  clearKeyed(relationListCache, relationListInflight)
  clearKeyed(detailContentCache, detailContentInflight)
}

function deleteContestKeysForSeason (sid) {
  delete contestInfoCache[`season:${sid}`]
  delete contestInfoInflight[`season:${sid}`]
  Object.keys(contestInfoCache).forEach(k => {
    if (k.indexOf('contest:') === 0) {
      const data = contestInfoCache[k]
      if (data && String(data.seasonId) === sid) delete contestInfoCache[k]
    }
  })
  Object.keys(contestInfoInflight).forEach(k => {
    if (k.indexOf('contest:') === 0) delete contestInfoInflight[k]
  })
}

/**
 * 清除赛季相关缓存（报名跳转前 / 需强制刷新时）
 * @param {string} [seasonId] 不传则清空全部
 */
export function invalidateActivityContestCache (seasonId) {
  if (seasonId == null || seasonId === '') {
    clearAllActivityHomeCaches()
    return
  }
  const sid = String(seasonId)
  deleteContestKeysForSeason(sid)
  clearKeyed(modeCache, modeInflight, `season:${sid}`)
  clearKeyed(unreadCache, unreadInflight, `season:${sid}`)
  clearKeyed(taskCardCache, taskCardInflight, `season:${sid}`)
  Object.keys(relationListCache).forEach(k => {
    if (k.indexOf(`season:${sid}`) === 0) delete relationListCache[k]
  })
  Object.keys(relationListInflight).forEach(k => {
    if (k.indexOf(`season:${sid}`) === 0) delete relationListInflight[k]
  })
  clearKeyed(detailContentCache, detailContentInflight)
}

export function invalidateActivityUnreadCache (seasonId) {
  if (seasonId == null || seasonId === '') {
    clearKeyed(unreadCache, unreadInflight)
    clearKeyed(unreadNoticeInflight, unreadNoticeInflight)
    return
  }
  const sid = String(seasonId)
  clearKeyed(unreadCache, unreadInflight, `season:${sid}`)
  Object.keys(unreadNoticeInflight).forEach(k => {
    if (k.indexOf(`season:${sid}:`) === 0) delete unreadNoticeInflight[k]
  })
}

export function invalidateActivityTaskCardCache (seasonId) {
  if (seasonId == null || seasonId === '') {
    clearKeyed(taskCardCache, taskCardInflight)
    return
  }
  clearKeyed(taskCardCache, taskCardInflight, `season:${String(seasonId)}`)
}

export function invalidateActivityModeCache (seasonId) {
  if (seasonId == null || seasonId === '') {
    clearKeyed(modeCache, modeInflight)
    return
  }
  clearKeyed(modeCache, modeInflight, `season:${String(seasonId)}`)
}

function ensureApiPost (vm) {
  return !!(vm && vm.$api && typeof vm.$api.post === 'function')
}

/**
 * 拉取活动季信息（缓存 + inflight 去重）
 * @returns {Promise<object>}
 */
export function fetchActivityContestInfo (vm, ids) {
  const seasonId = ids && ids.seasonId ? String(ids.seasonId) : ''
  const contestId = ids && ids.contestId ? String(ids.contestId) : ''
  const key = contestCacheKey({ seasonId, contestId })
  if (!key) return Promise.reject(new Error('missing seasonId/contestId'))
  if (contestInfoCache[key]) return Promise.resolve(contestInfoCache[key])
  if (seasonId && contestInfoCache[`season:${seasonId}`]) {
    return Promise.resolve(contestInfoCache[`season:${seasonId}`])
  }
  if (contestInfoInflight[key]) return contestInfoInflight[key]

  const payload = seasonId ? { seasonId } : { contestId }
  const promise = new Promise((resolve, reject) => {
    if (!ensureApiPost(vm)) {
      reject(new Error('vm.$api.post unavailable'))
      return
    }
    const clearInflight = () => { delete contestInfoInflight[key] }
    vm.$api.post(
      'NEW_CONTEST_SEASON_GETINFO',
      payload,
      res => {
        clearInflight()
        const data = res && res.data
        if (!data) {
          reject(new Error('empty contest info'))
          return
        }
        const sid = data.seasonId != null ? String(data.seasonId) : ''
        if (sid) contestInfoCache[`season:${sid}`] = data
        if (contestId) contestInfoCache[`contest:${contestId}`] = data
        if (data.contestId != null) {
          contestInfoCache[`contest:${String(data.contestId)}`] = data
        }
        resolve(data)
      },
      err => {
        clearInflight()
        reject(err || new Error('GETINFO failed'))
      },
      err => {
        clearInflight()
        reject(err || new Error('GETINFO failed'))
      }
    )
  })
  contestInfoInflight[key] = promise
  return promise
}

/**
 * 体验/学生模式（NEW_TASKRELATION_GETMODE）
 * @returns {Promise<{ isExperience: number, isSkter: boolean }>}
 */
export function fetchActivityMode (vm, seasonId) {
  const sid = seasonId != null ? String(seasonId) : ''
  if (!sid) return Promise.reject(new Error('missing seasonId'))
  const key = `season:${sid}`
  if (modeCache[key]) return Promise.resolve(modeCache[key])
  if (modeInflight[key]) return modeInflight[key]

  const promise = new Promise((resolve, reject) => {
    if (!ensureApiPost(vm)) {
      reject(new Error('vm.$api.post unavailable'))
      return
    }
    const clearInflight = () => { delete modeInflight[key] }
    vm.$api.post(
      'NEW_TASKRELATION_GETMODE',
      { seasonId: sid },
      res => {
        clearInflight()
        const data = (res && res.data) || {}
        const parsed = {
          isExperience: +(data.isExperience) || 0,
          isSkter: !!(
            data.isSkter === true ||
            data.isSkter === 1 ||
            data.isSkter === '1' ||
            data.isSkterUser === true ||
            data.isSkterUser === 1
          )
        }
        modeCache[key] = parsed
        resolve(parsed)
      },
      err => {
        clearInflight()
        reject(err || new Error('GETMODE failed'))
      },
      err => {
        clearInflight()
        reject(err || new Error('GETMODE failed'))
      }
    )
  })
  modeInflight[key] = promise
  return promise
}

/**
 * 未读数（GET_UNREADCOUNT）
 * 仅并发去重（inflight）：同一次刷新内并发调用只发一次真实 HTTP；
 * 不做结果缓存（跳转路由需要重新请求最新数据）
 * @returns {Promise<{ activityNum: number, noticeNum: number, total: number }>}
 */
export function fetchActivityUnreadCount (vm, seasonId) {
  const sid = seasonId != null ? String(seasonId) : ''
  if (!sid) return Promise.reject(new Error('missing seasonId'))
  const key = `season:${sid}`
  if (unreadInflight[key]) return unreadInflight[key]

  const promise = new Promise((resolve, reject) => {
    if (!ensureApiPost(vm)) {
      reject(new Error('vm.$api.post unavailable'))
      return
    }
    const clearInflight = () => { delete unreadInflight[key] }
    vm.$api.post(
      'GET_UNREADCOUNT',
      { businessId: sid },
      res => {
        clearInflight()
        const d = (res && res.data) || {}
        const activityNum = +d.activityNum || 0
        const noticeNum = +d.noticeNum || 0
        const parsed = {
          activityNum,
          noticeNum,
          total: activityNum + noticeNum
        }
        resolve(parsed)
      },
      err => {
        clearInflight()
        reject(err || new Error('UNREADCOUNT failed'))
      },
      err => {
        clearInflight()
        reject(err || new Error('UNREADCOUNT failed'))
      }
    )
  })
  unreadInflight[key] = promise
  return promise
}

/**
 * 未读公告列表（COMMON_NOTICE_UNREAD = /api/notice/getUnreadNotice）
 * 仅并发去重（inflight）：同一次刷新内并发调用只发一次真实 HTTP；
 * 不做结果缓存（跳转路由需要重新请求最新数据）
 * @param {any} vm Vue 组件实例（提供 $api.post）
 * @param {string|number} topicId seasonId（公告的 topicId）
 * @param {number} [pageSize=3] 取几条（all-btn 取 3 条，够用）
 * @returns {Promise<{ noticeList: Array<{noticeId:string|number,noticeTitle:string,publishTimeStr:string,isRead:number,[k:string]:any}>, userLatestNoticeInfo: {noticeId?:string|number,isRead?:number,[k:string]:any}|null }>}
 */
export function fetchActivityUnreadNoticeList (vm, topicId, pageSize) {
  const tid = topicId != null ? String(topicId) : ''
  if (!tid) return Promise.reject(new Error('missing topicId'))
  const ps = Number(pageSize) > 0 ? Number(pageSize) : 3
  const key = `season:${tid}:ps${ps}`
  if (unreadNoticeInflight[key]) return unreadNoticeInflight[key]

  const promise = new Promise((resolve, reject) => {
    if (!ensureApiPost(vm)) {
      reject(new Error('vm.$api.post unavailable'))
      return
    }
    const clearInflight = () => { delete unreadNoticeInflight[key] }
    vm.$api.post(
      'COMMON_NOTICE_UNREAD',
      { topicId: tid, startIndex: 0, pageSize: ps },
      res => {
        clearInflight()
        const d = (res && res.data) || {}
        const list = (d.noticeList || []).map(e => ({
          ...e,
          isRead: e && e.isRead == null ? 0 : Number(e.isRead) || 0
        }))
        const parsed = {
          noticeList: list,
          userLatestNoticeInfo: d.userLatestNoticeInfo || null
        }
        resolve(parsed)
      },
      err => {
        clearInflight()
        reject(err || new Error('COMMON_NOTICE_UNREAD failed'))
      },
      err => {
        clearInflight()
        reject(err || new Error('COMMON_NOTICE_UNREAD failed'))
      }
    )
  })
  unreadNoticeInflight[key] = promise
  return promise
}

/**
 * 任务卡片列表（GETTASKCARDLIST）→ sectionList
 * @param {{ force?: boolean }} [opts]
 * @returns {Promise<Array>}
 */
export function fetchActivityTaskCardList (vm, seasonId, opts) {
  const sid = seasonId != null ? String(seasonId) : ''
  if (!sid) return Promise.reject(new Error('missing seasonId'))
  const key = `season:${sid}`
  if (opts && opts.force) {
    clearKeyed(taskCardCache, taskCardInflight, key)
  }
  if (taskCardCache[key]) return Promise.resolve(taskCardCache[key])
  if (taskCardInflight[key]) return taskCardInflight[key]

  const promise = new Promise((resolve, reject) => {
    if (!ensureApiPost(vm)) {
      reject(new Error('vm.$api.post unavailable'))
      return
    }
    const clearInflight = () => { delete taskCardInflight[key] }
    vm.$api.post(
      'NEW_CONTEST_TASKRELATION_USER_GETTASKCARDLIST',
      { seasonId: sid },
      res => {
        clearInflight()
        const list = (res && res.data && res.data.sectionList) || []
        taskCardCache[key] = list
        resolve(list)
      },
      err => {
        clearInflight()
        reject(err || new Error('GETTASKCARDLIST failed'))
      },
      err => {
        clearInflight()
        reject(err || new Error('GETTASKCARDLIST failed'))
      }
    )
  })
  taskCardInflight[key] = promise
  return promise
}

/**
 * 详情分类列表（RELATION_LIST）
 * PC 活动详情现网 topicType=1；可传入覆盖
 * @returns {Promise<Array>}
 */
export function fetchActivityRelationList (vm, seasonId, topicType) {
  const sid = seasonId != null ? String(seasonId) : ''
  if (!sid) return Promise.reject(new Error('missing seasonId'))
  const tt = topicType == null ? 1 : topicType
  const key = `season:${sid}:t${tt}`
  if (relationListCache[key]) return Promise.resolve(relationListCache[key])
  if (relationListInflight[key]) return relationListInflight[key]

  const promise = new Promise((resolve, reject) => {
    if (!ensureApiPost(vm)) {
      reject(new Error('vm.$api.post unavailable'))
      return
    }
    const clearInflight = () => { delete relationListInflight[key] }
    vm.$api.post(
      'NEW_CONTEST_DETAIL_RELATION_LIST',
      { seasonId: sid, topicType: tt },
      res => {
        clearInflight()
        const list = (res && res.data && res.data.list) || []
        relationListCache[key] = list
        resolve(list)
      },
      err => {
        clearInflight()
        reject(err || new Error('RELATION_LIST failed'))
      },
      err => {
        clearInflight()
        reject(err || new Error('RELATION_LIST failed'))
      }
    )
  })
  relationListInflight[key] = promise
  return promise
}

/**
 * 详情内容块（DETAIL_GET）→ 解析后的 detailContent 数组
 * @returns {Promise<Array>}
 */
export function fetchActivityDetailGet (vm, detailId) {
  const id = detailId != null ? String(detailId) : ''
  if (!id) return Promise.reject(new Error('missing detailId'))
  const key = `detail:${id}`
  if (detailContentCache[key]) return Promise.resolve(detailContentCache[key])
  if (detailContentInflight[key]) return detailContentInflight[key]

  const promise = new Promise((resolve, reject) => {
    if (!ensureApiPost(vm)) {
      reject(new Error('vm.$api.post unavailable'))
      return
    }
    const clearInflight = () => { delete detailContentInflight[key] }
    vm.$api.post(
      'NEW_CONTEST_DETAIL_GET',
      { detailId: id },
      res => {
        clearInflight()
        let list = []
        try {
          const raw = res && res.data && res.data.detailContent
          list = raw ? JSON.parse(raw) : []
          if (!Array.isArray(list)) list = []
        } catch (e) {
          list = []
        }
        detailContentCache[key] = list
        resolve(list)
      },
      err => {
        clearInflight()
        reject(err || new Error('DETAIL_GET failed'))
      },
      err => {
        clearInflight()
        reject(err || new Error('DETAIL_GET failed'))
      }
    )
  })
  detailContentInflight[key] = promise
  return promise
}



// WEBPACK FOOTER //
// ./src/utils/activityHome.js